import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PropertyCard from '../../components/FeaturedHomes/card';
import useAuth from '../../hooks/useAuth';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import Swal from 'sweetalert2';

const HeroSection = styled.div`
  background: url('/backgrounds/castelos.webp') center/cover no-repeat;
  text-align: center;
  padding: 100px 20px;
  margin-bottom: 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 0;
  }

  h1, p {
    position: relative;
    z-index: 1;
  }

  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 20px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    color: white;
  }

  p {
    font-size: 1.5rem;
    font-weight: 400;
    max-width: 800px;
    margin: 0 auto;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
    color: white;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;

    h1 {
      font-size: 2rem;
    }

    p {
      font-size: 1.125rem;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  padding: 16px 0 40px;

  button {
    padding: 8px 16px;
    border-radius: 8px;
    border: 2px solid #eee;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:enabled {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  span {
    color: #555;
    font-weight: 500;
  }
`;

const SearchBar = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TabGroup = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px;
  background: #f5f5f5;
  border-radius: 8px;

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    background: none;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: #34574A;
      color: white;
    }
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  min-width: 200px;

  input {
    width: 100%;
    padding: 12px;
    padding-left: 40px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const PropertyType = styled.div`
  position: relative;
  min-width: 200px;

  .select-header {
    width: 100%;
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #333;

    &:hover {
      border-color: #34574A;
    }
  }

  .select-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    opacity: ${props => props.isOpen ? 1 : 0};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transform: translateY(${props => props.isOpen ? '0' : '-10px'});
    transition: all 0.3s ease;
    max-height: 260px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .select-option {
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #333;

    &:hover {
      background: #f5f5f5;
    }

    &:first-child {
      border-radius: 8px 8px 0 0;
    }

    &:last-child {
      border-radius: 0 0 8px 8px;
    }
  }
`;

const PriceRange = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 300px;

  select {
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    appearance: none;
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }

  input {
    width: 120px;
    padding: 12px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #34574A;
      outline: none;
    }
  }
`;

const SearchButton = styled.button`
  padding: 12px 24px;
  background: #34574A;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #2a4539;
  }
`;

const Favorites = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [favoritos, setFavoritos] = useState([]);
  const [imoveis, setImoveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const { isLoggedIn, user } = useAuth();
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});

  // Ver fotos (lightbox simples com SweetAlert2)
  const handleViewPhotos = (property) => {
    try {
      // Normaliza array de imagens em URLs
      let raw = [];
      if (Array.isArray(property.fotos) && property.fotos.length) raw = property.fotos;
      else if (Array.isArray(property.imagens) && property.imagens.length) raw = property.imagens;
      const imgs = raw
        .map(x => (typeof x === 'string' ? x : (x?.urlFOTO || x?.pathFOTO || '')))
        .filter(Boolean);
      if (!imgs.length) return;
      let idx = 0;
      const renderHtml = (src, index, total) => `
        <div style="position:relative; display:flex; align-items:center; justify-content:center; background:#111; padding:0; margin:0;">
          <button id="lb-prev" style="position:absolute; left:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❮</button>
          <img id="lb-img" src="${src}" style="max-width:90vw; max-height:80vh; object-fit:contain; border-radius:4px;" />
          <button id="lb-next" style="position:absolute; right:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❯</button>
          <div id="lb-counter" style="position:absolute; bottom:12px; left:50%; transform:translateX(-50%); background:rgba(0,0,0,.6); color:#fff; padding:6px 10px; border-radius:12px; font-weight:600;">${index+1} / ${total}</div>
        </div>`;
      const update = (swalContent) => {
        const img = swalContent.querySelector('#lb-img');
        const counter = swalContent.querySelector('#lb-counter');
        if (img) img.src = imgs[idx];
        if (counter) counter.textContent = `${idx + 1} / ${imgs.length}`;
      };
      Swal.fire({
        html: renderHtml(imgs[idx], idx, imgs.length),
        width: '90%', padding: 0, background: '#111',
        showConfirmButton: false, showCloseButton: true, allowOutsideClick: false,
        didOpen: (el) => {
          const prev = el.querySelector('#lb-prev');
          const next = el.querySelector('#lb-next');
          const onPrev = () => { idx = (idx - 1 + imgs.length) % imgs.length; update(el); };
          const onNext = () => { idx = (idx + 1) % imgs.length; update(el); };
          prev?.addEventListener('click', onPrev);
          next?.addEventListener('click', onNext);
        }
      });
    } catch (e) { console.error(e); }
  };

  // Ver vídeo (aceita iframe embed ou URL e converte para embed)
  const handleViewVideo = (property) => {
    let raw = (property?.videoIMOVEL || '').trim();
    if (!raw) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'info', title: 'Imóvel sem vídeo' }); } catch (e) { console.error(e); }
      return;
    }

    // Se já é um iframe, usa como está
    const hasIframe = /<\s*iframe[\s\S]*><\s*\/\s*iframe\s*>/i.test(raw) || /<\s*iframe[^>]*>/i.test(raw);

    // Converte URLs do YouTube/Vimeo para embed
    const toEmbed = (url) => {
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        if (host.includes('youtube.com')) {
          // formatos: /watch?v=ID, /shorts/ID, /embed/ID
          const v = u.searchParams.get('v');
          let id = v;
          if (!id && u.pathname.startsWith('/shorts/')) id = u.pathname.split('/shorts/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/embed/')) id = u.pathname.split('/embed/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/watch')) id = v;
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('youtu.be')) {
          const id = u.pathname.slice(1).split(/[?&#]/)[0];
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('vimeo.com')) {
          const id = u.pathname.replace(/\/(video|channels|ondemand)/, '').split('/').filter(Boolean)[0];
          if (id) return `https://player.vimeo.com/video/${id}`;
          return url;
        }
      } catch (e) { console.error(e); }
      return url;
    };

    let contentHtml = raw;
    if (!hasIframe) {
      const embedSrc = toEmbed(raw);
      contentHtml = `
        <div style="position:relative;padding-top:56.25%;width:100%;">
          <iframe src="${embedSrc}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"></iframe>
        </div>`;
    }

    Swal.fire({
      title: 'Vídeo do imóvel',
      html: `<div style="padding:0;position:relative;">${contentHtml}</div>`,
      width: '80%', padding: 0, background: '#000', showConfirmButton: false, showCloseButton: true
    });
  };

  // Comparar (adiciona ao localStorage e opcionalmente navega)
  const handleCompare = (property) => {
    const absolutize = (src) => {
      if (!src || typeof src !== 'string') return '';
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      const cleaned = src.replace(/^\.*\/+/, '').replace(/^images\//, 'images/');
      const host = (import.meta.env.VITE_PUBLIC_ASSETS_HOST || '').replace(/\/$/, '');
      const base = host || String(import.meta.env.VITE_URL_API || '').replace(/\/api\/?$/, '');
      return `${base}/${cleaned}`;
    };
    const firstPhotoRaw = Array.isArray(property.fotos) && property.fotos.length
      ? (typeof property.fotos[0] === 'string' ? property.fotos[0] : (property.fotos[0]?.urlFOTO || property.fotos[0]?.pathFOTO || ''))
      : '';
    const mapped = {
      id: property.codigoIMOVEL || property.id,
      title: property.tituloIMOVEL || 'Sem título',
      price: property.valorIMOVEL ?? '',
      location: property.enderecoIMOVEL || '',
      bedrooms: property.quartosIMOVEL ?? '',
      bathrooms: property.banheirosIMOVEL ?? '',
      area: property.metragemIMOVEL ?? '',
      garage: property.vagasgaragemIMOVEL ?? '',
      image: absolutize(firstPhotoRaw)
    };
    try {
      const prev = JSON.parse(localStorage.getItem('compare_items') || '[]');
      const exists = prev.some(p => String(p.id) === String(mapped.id));
      const next = exists ? prev : [...prev, mapped];
      localStorage.setItem('compare_items', JSON.stringify(next));
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false, icon: 'success', title: 'Adicionado à comparação' });
      if (next.length >= 2) {
        // Sugere ir comparar quando já tem 2 ou mais
        setTimeout(() => navigate('/compare', { state: { items: next } }), 250);
      }
    } catch (e) { console.error(e); }
  };

  // Descobre o usuário logado a partir do localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const id = u?.id || u?.idUSUARIO || u?.codigoUSUARIO || u?.userId || u?.usuarioId || null;
        setUserId(id);
      } else {
        setUserId(null);
      }
    } catch {
      setUserId(null);
    }
  }, []);


  // useEffect removido - não precisamos mais do propertyTypeRef

  // Carrega favoritos + imóveis populados via novo endpoint
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Usuário não autenticado');
      setLoading(false);
      return;
    }
    // Não dispara erro cedo: user/userId podem demorar a hidratar
    const uid = (user?.codigo || user?.id || user?.userId || userId || null);
    if (!uid) {
      setLoading(true);
      return;
    }

    setLoading(true);
    window.dispatchEvent(new Event('favoritesChanged'));

    const normalize = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.Registros)) return payload.Registros;
      if (payload && typeof payload === 'object') return [payload];
      return [];
    };

    const headers = {
      accept: 'application/json',
      Authorization: `Bearer ${token}`
    };

    (async () => {
      try {
        const resFav = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${uid}`, { headers });
        if (!resFav.ok) throw new Error('Erro ao buscar favoritos');
        const jsonFav = await resFav.json();
        console.log('JSON completo da API de favoritos:', jsonFav);
        const favArr = normalize(jsonFav);
        console.log('Array de favoritos normalizado:', favArr);

        const enriched = await Promise.all(
          favArr.map(async (fav) => {
            console.log('Favorito completo:', fav);
            console.log('Chaves do favorito:', Object.keys(fav || {}));
            console.log('Imóvel associado:', fav.imovel);
            console.log('Chaves do imóvel:', Object.keys(fav.imovel || {}));
            if (fav?.imovel) {
              // Já tem imóvel, mas vamos verificar se tem dadosBasicos com preço
              console.log('Imóvel já presente, verificando dadosBasicos:', fav.imovel?.dadosBasicos);
              console.log('Preço em dadosBasicos:', fav.imovel?.dadosBasicos?.valorIMOVEL);
              return fav;
            }
            const imovelId = fav?.imovelFAVORITO || fav?.codigoIMOVEL || fav?.idIMOVEL || null;
            if (!imovelId) return fav;
            try {
              const resIm = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${imovelId}`, { headers });
              if (!resIm.ok) return fav;
              const jsonIm = await resIm.json();
              console.log('JSON do imóvel individual:', jsonIm);
              const imovel = Array.isArray(jsonIm?.data) ? jsonIm.data[0] : (Array.isArray(jsonIm) ? jsonIm[0] : (jsonIm?.data || jsonIm));
              console.log('Imóvel extraído:', imovel);
              console.log('dadosBasicos:', imovel?.dadosBasicos);
              console.log('Chaves de dadosBasicos:', Object.keys(imovel?.dadosBasicos || {}));
              console.log('Preço em dadosBasicos:', imovel?.dadosBasicos?.valorIMOVEL);
              console.log('enderecos:', imovel?.enderecos);
              console.log('midia:', imovel?.midia);
              console.log('categoria:', imovel?.categoria);
              console.log('caracteristicas:', imovel?.caracteristicas);
              console.log('fotos (nível raiz):', imovel?.fotos);
              return imovel ? { ...fav, imovel } : fav;
            } catch {
              return fav;
            }
          })
        );
        console.log('Favoritos enriquecidos:', enriched);

        // Alinha lista com contador do header: só mantém favoritos com id de imóvel válido e remove duplicados
        const getImovelId = (fav) => (
          fav?.imovelFAVORITO ||
          fav?.codigoIMOVEL ||
          fav?.idIMOVEL ||
          fav?.imovel?.codigoIMOVEL ||
          fav?.imovel?.id ||
          null
        );
        const valid = enriched.filter((fav) => !!getImovelId(fav));
        const seen = new Set();
        const deduped = valid.filter((fav) => {
          const key = String(getImovelId(fav));
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setFavoritos(deduped);
        setImoveis([]);
        setLoading(false);
      } catch {
        setError('Erro ao buscar favoritos');
        setFavoritos([]);
        setLoading(false);
      }
    })();
  }, [user, userId]);

  // Relaciona favorito ao imóvel e prioriza sempre os dados do imóvel real (IGUAL TELA INTERNA)
  const imoveisArr = Array.isArray(imoveis) ? imoveis : (imoveis?.data || imoveis?.imoveis || []);
  const favoritosComImovel = favoritos.map(fav => {
    const safeFav = (fav && typeof fav === 'object') ? fav : {};
    const imovel = safeFav?.imovel || imoveisArr.find(i => String(i.codigoIMOVEL || i.id) === String(safeFav?.imovelFAVORITO));
    const fallbackImovel = {
      codigoIMOVEL: safeFav?.imovelFAVORITO || safeFav?.codigoIMOVEL || safeFav?.idIMOVEL || safeFav?.id || null,
      slugIMOVEL: safeFav?.slugIMOVEL || safeFav?.slug || '',
      tituloIMOVEL: safeFav?.tituloIMOVEL || safeFav?.titulo || safeFav?.nomeIMOVEL || 'Imóvel favorito',
      enderecoIMOVEL: safeFav?.enderecoIMOVEL || safeFav?.localizacaoIMOVEL || '',
      cityIMOVEL: safeFav?.cityIMOVEL || safeFav?.cidadeIMOVEL || safeFav?.cidade || '',
      // PRIORIDADE ABSOLUTA: valorIMOVEL (consistente com página pública)
      valorIMOVEL: safeFav?.valorIMOVEL ?? safeFav?.precoIMOVEL ?? safeFav?.valor ?? safeFav?.preco ?? 0,
      fotos: Array.isArray(safeFav?.fotos) ? safeFav.fotos : [],
      imagemIMOVEL: safeFav?.imagemIMOVEL || '',
      quartosIMOVEL: safeFav?.quartosIMOVEL || safeFav?.dormitoriosIMOVEL || safeFav?.quartos || '-',
      banheirosIMOVEL: safeFav?.banheirosIMOVEL || safeFav?.bathroomsIMOVEL || safeFav?.banheiros || '-',
      vagasIMOVEL: safeFav?.vagasIMOVEL || safeFav?.vagasGaragemIMOVEL || safeFav?.vagas || '-',
      areaIMOVEL: safeFav?.areaIMOVEL || safeFav?.metrosIMOVEL || safeFav?.area || '-',
    };
    const chosen = imovel || safeFav?.imovel || fallbackImovel;
    return {
      ...safeFav,
      imovel: chosen,
      tituloIMOVEL: chosen.tituloIMOVEL || chosen.title || chosen.nomeIMOVEL || safeFav?.tituloIMOVEL || safeFav?.titulo || 'Imóvel favorito',
      imagemIMOVEL: (chosen.fotos && chosen.fotos.length > 0) ? (chosen.fotos[0].urlFOTO || chosen.fotos[0].pathFOTO || chosen.fotos[0].url || chosen.fotos[0].path) : (chosen.imagemIMOVEL || ''),
      localizacaoIMOVEL: chosen.enderecoIMOVEL || chosen.localizacaoIMOVEL || chosen.cityIMOVEL || chosen.cidadeIMOVEL || chosen.cidade || safeFav?.localizacaoIMOVEL || '',
      // PRIORIDADE ABSOLUTA: valorIMOVEL (consistente com página pública)
      valorIMOVEL: chosen.valorIMOVEL ?? chosen.precoIMOVEL ?? chosen.valor ?? chosen.preco ?? safeFav?.valorIMOVEL ?? safeFav?.precoIMOVEL ?? 0,
    };
  });

  const filteredProperties = favoritosComImovel
    .map(fav => {
      const imovel = fav.imovel || {};
      console.log('Imóvel favorito recebido:', imovel);
      console.log('Preço recebido:', imovel.preco);
      console.log('Preço alternativo:', imovel.precoIMOVEL);
      console.log('Valor IMOVEL:', imovel.valorIMOVEL);
      console.log('Todas as chaves do imóvel:', Object.keys(imovel));
      console.log('dadosBasicos:', imovel?.dadosBasicos);
      console.log('enderecos:', imovel?.enderecos);
      console.log('midia:', imovel?.midia);
      console.log('categoria:', imovel?.categoria);
      console.log('caracteristicas:', imovel?.caracteristicas);

      // Extrair dados dos objetos aninhados
      const dadosBasicos = imovel?.dadosBasicos || {};
      const enderecos = imovel?.enderecos || {};
      const midia = imovel?.midia || {};
      const categoria = imovel?.categoria || {};
      const caracteristicas = imovel?.caracteristicas || {};
      const fotosRaiz = imovel?.fotos || [];

      console.log('Dados básicos:', dadosBasicos);
      console.log('Chaves de dadosBasicos:', Object.keys(dadosBasicos || {}));
      console.log('Endereços:', enderecos);
      console.log('Mídia:', midia);
      console.log('Categoria:', categoria);
      console.log('Características:', caracteristicas);
      console.log('fotos (nível raiz):', imovel?.fotos);
      console.log('Valor em dadosBasicos:', dadosBasicos?.valorIMOVEL);
      console.log('Label valor em dadosBasicos:', dadosBasicos?.labelvalorIMOVEL);

      const mapped = {
        ...imovel,
        codigoFAVORITO: fav.codigoFAVORITO,
        ativoFAVORITO: fav.ativoFAVORITO,
        codigoIMOVEL: imovel.codigoIMOVEL || imovel.id || fav.codigoIMOVEL || fav.imovelFAVORITO,
        slugIMOVEL: dadosBasicos?.slugIMOVEL || imovel.slugIMOVEL || imovel.slug || fav.slugIMOVEL || fav.slug || '',
        tituloIMOVEL: dadosBasicos?.tituloIMOVEL || imovel.tituloIMOVEL || imovel.title || fav.tituloIMOVEL || fav.titulo || 'Imóvel favorito',
        imagemIMOVEL: (fotosRaiz && fotosRaiz.length > 0) ? (fotosRaiz[0].urlFOTO || fotosRaiz[0].pathFOTO || fotosRaiz[0].url || fotosRaiz[0].path) : ((midia?.fotos && midia.fotos.length > 0) ? (midia.fotos[0].urlFOTO || midia.fotos[0].pathFOTO || midia.fotos[0].url || midia.fotos[0].path) : (midia?.imagemIMOVEL || imovel.imagemIMOVEL || fav.imagemIMOVEL || '')),
        localizacaoIMOVEL: enderecos?.enderecoIMOVEL || enderecos?.localizacaoIMOVEL || enderecos?.cityIMOVEL || enderecos?.cidadeIMOVEL || enderecos?.cidade || imovel.enderecoIMOVEL || imovel.localizacaoIMOVEL || imovel.cityIMOVEL || imovel.cidadeIMOVEL || imovel.cidade || fav.localizacaoIMOVEL || fav.enderecoIMOVEL || '',
        // PRIORIDADE ABSOLUTA: dadosBasicos.valorIMOVEL (igual à página pública)
        valorIMOVEL: dadosBasicos?.valorIMOVEL ?? imovel.valorIMOVEL ?? imovel.precoIMOVEL ?? imovel.valor ?? imovel.preco ?? fav.valorIMOVEL ?? fav.precoIMOVEL ?? 0,
        labelvalorIMOVEL: dadosBasicos?.labelvalorIMOVEL || imovel.labelvalorIMOVEL || '',
        tipoIMOVEL: categoria?.tipo?.codigoTipo || categoria?.tipo?.codigoTIPOIMOVEL || categoria?.tipoCATEGORIA || imovel.tipoIMOVEL || '',
        quartosIMOVEL: caracteristicas?.quartosIMOVEL || imovel.quartosIMOVEL || imovel.dormitoriosIMOVEL || imovel.bedroomsIMOVEL || imovel.quartos || '-',
        banheirosIMOVEL: caracteristicas?.banheirosIMOVEL || imovel.banheirosIMOVEL || imovel.bathroomsIMOVEL || imovel.banheiros || '-',
        vagasIMOVEL: caracteristicas?.vagasGaragemIMOVEL || imovel.vagasIMOVEL || imovel.vagasGaragemIMOVEL || imovel.parkingSpaces || imovel.vagas || '-',
        areaIMOVEL: caracteristicas?.metragemIMOVEL || imovel.areaIMOVEL || imovel.metrosIMOVEL || imovel.area || '-',
      };
      console.log('Objeto mapeado para o card:', mapped);
      return mapped;
    })
    .filter(property => {
      // Filtro de busca por título ou localização
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (property.tituloIMOVEL && property.tituloIMOVEL.toLowerCase().includes(searchLower)) ||
        (property.localizacaoIMOVEL && property.localizacaoIMOVEL.toLowerCase().includes(searchLower));
      return matchesSearch;
    });

  const handleRemoveFavorite = async (codigoFAVORITO) => {
    setShowDeleteModal(codigoFAVORITO);
  };

  const confirmRemoveFavorite = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${import.meta.env.VITE_URL_API}/favoritos/${showDeleteModal}`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      setFavoritos(favoritos.filter(fav => fav.codigoFAVORITO !== showDeleteModal));
      setShowDeleteModal(null);
      // Dispara evento para atualizar contadores
      window.dispatchEvent(new Event('favoritesChanged'));
    } catch (err) {
      setError('Erro ao remover favorito');
      setShowDeleteModal(null);
    }
  };

  if (!isLoggedIn || !user) {
    return (
      <CategoryPage>
        <NoResults>
          <h3>Autenticação necessária</h3>
          <p>Faça login para ver seus favoritos.</p>
          <button onClick={handleOpenLogin} style={{ marginTop: 16, padding: '12px 24px', borderRadius: 8, background: '#BA824F', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>Fazer login</button>
        </NoResults>
      </CategoryPage>
    );
  }

  return (
    <>
      <HeroSection>
        <h1>Os meus Favoritos</h1>
        <p>Explore os imóveis que você salvou</p>
      </HeroSection>

      <CategoryPage>
        <SearchBar style={{ marginBottom: 24 }}>
          <input
            type="text"
            placeholder="Buscar por título ou localização..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, padding: '12px 16px', border: '2px solid #eee', borderRadius: 8, fontSize: '1rem' }}
          />
        </SearchBar>

        {error && <div style={{ color: '#B00020', marginBottom: 18 }}>{error}</div>}
        {loading ? (
          <div style={{ textAlign: 'center', marginTop: 32 }}>Carregando...</div>
        ) : filteredProperties.length > 0 ? (
          <PropertiesGrid>
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.codigoFAVORITO}
                property={property}
                isFavorited={true}
                onFavorite={() => {}}
                onUnfavorite={() => handleRemoveFavorite(property.codigoFAVORITO)}
                onViewPhotos={handleViewPhotos}
                onViewVideo={handleViewVideo}
                onCompare={handleCompare}
              />
            ))}
          </PropertiesGrid>
        ) : (
          <NoResults>
            <h3>Nenhum favorito encontrado.</h3>
            <p>Adicione imóveis aos seus favoritos para vê-los aqui.</p>
          </NoResults>
        )}
        
        {/* Modal de confirmação de exclusão */}
        {showDeleteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', padding: '36px 32px 28px 32px', borderRadius: 16, minWidth: 340, boxShadow: '0 8px 32px rgba(52,87,74,0.18)' }}>
              <h3 style={{ margin: '0 0 18px 0', fontSize: '1.32rem', color: '#34574A', fontWeight: 700, textAlign: 'center' }}>Excluir Favorito</h3>
              <div style={{color:'#B00020',textAlign:'center',marginBottom:18,fontWeight:600}}>Tem certeza que deseja excluir este favorito?</div>
              <div style={{ display: 'flex', gap: 16, marginTop: 18, justifyContent: 'center' }}>
                <button onClick={confirmRemoveFavorite} style={{ background: '#B00020', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 26px', fontSize: '1.08rem', fontWeight: 700, cursor: 'pointer' }}>Excluir</button>
                <button onClick={()=>setShowDeleteModal(null)} style={{ background: '#34574A', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 26px', fontSize: '1.08rem', fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          </div>
        )}
      </CategoryPage>
    </>
  );
};

// Badge para exibir moeda global
const CurrencyBadge = styled.div`
  padding: 10px 14px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-weight: 600;
  color: #333;
  min-width: 64px;
  text-align: center;
`;

const CategoryPage = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 60px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: #333;
  }

  p {
    font-size: 1rem;
    max-width: 500px;
    margin: 0 auto;
  }
`;

export default Favorites;
